import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { Login } from "./pages/auth/Login";
import { SignUp } from "./pages/auth/SignUp";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { AuthCallback } from "./pages/auth/AuthCallback";
import { Subscribe } from "./pages/Subscribe";
import { ClientDashboard } from "./pages/client/ClientDashboard";
import { ClientProjectDetail } from "./pages/client/ClientProjectDetail";
import { ClientProfile } from "./pages/client/ClientProfile";
import { Onboarding } from "./pages/client/Onboarding";
import { theme } from "./theme/theme";
import { ContractorDashboard } from "./pages/contractor/ContractorDashboard";
import { ContractorProjectDetail } from "./pages/contractor/ContractorProjectDetail";
import { ContractorProfile } from "./pages/contractor/ContractorProfile";
import { Settings } from "./pages/Settings";
import { TermsOfService } from "./pages/legal/TermsOfService";
import { PrivacyPolicy } from "./pages/legal/PrivacyPolicy";

function RootRedirect() {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/login" replace />;
  }

  if (profile.role === "contractor") {
    return <Navigate to="/contractor" replace />;
  } else {
    return <Navigate to="/client" replace />;
  }
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />

            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/onboarding" element={<Onboarding />} />

            {/* Subscription management — accessible any time when logged in */}
            <Route
              path="/subscribe"
              element={
                <ProtectedRoute>
                  <Subscribe />
                </ProtectedRoute>
              }
            />

            {/* Contractor routes */}
            <Route
              path="/contractor"
              element={
                <ProtectedRoute requiredRole="contractor">
                  <ContractorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contractor/projects/:projectId"
              element={
                <ProtectedRoute requiredRole="contractor">
                  <ContractorProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contractor/profile"
              element={
                <ProtectedRoute requiredRole="contractor">
                  <ContractorProfile />
                </ProtectedRoute>
              }
            />

            {/* Client routes */}
            <Route
              path="/client"
              element={
                <ProtectedRoute requiredRole="client">
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/projects/:projectId"
              element={
                <ProtectedRoute requiredRole="client">
                  <ClientProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/profile"
              element={
                <ProtectedRoute requiredRole="client">
                  <ClientProfile />
                </ProtectedRoute>
              }
            />

            {/* Settings — shared page, works for both roles */}
            <Route
              path="/contractor/settings"
              element={
                <ProtectedRoute requiredRole="contractor">
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/settings"
              element={
                <ProtectedRoute requiredRole="client">
                  <Settings />
                </ProtectedRoute>
              }
            />

            {/* Legal — public, no auth required */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
